import {
  collection,
  error,
  evaluateCellFormula,
  fromCell,
  type FormulaReference,
  type FormulaResolveIntent,
  type FormulaSyntaxError,
  type FormulaValue,
} from './language'
import { fromStored, type StoredFormula } from './stored'

/** Persistence-neutral states understood by the formula runtime. */
export type RuntimeCellOverride =
  | { kind: 'none' }
  | { kind: 'value'; value: number | string }
  | { kind: 'blank' }
  | { kind: 'formula'; formula: StoredFormula }

export interface FormulaCellCoordinate {
  sheetId: string
  rowId: string
  field: string
}

/**
 * One immutable sheet snapshot. `rows` and `fields` are already in their logical persisted
 * order: temporary sorting and filtering must never be passed here.
 */
export interface CellFormulaRuntimeSheet<Row extends object> {
  id: string
  rows: readonly Row[]
  fields: readonly string[]
  rowId: (row: Row) => string | undefined
  rawValue: (row: Row, field: string) => unknown
  /** The stored calculated-column/base-column formula, if the field carries one. */
  formulaForField?: (field: string) => StoredFormula | undefined
  overrideFor?: (rowId: string, field: string) => RuntimeCellOverride | undefined
  /** True when `field` is this row's input for a cycle of typeable columns: it shows what is
   *  stored instead of its formula. */
  cycleInput?: (row: Row, field: string) => boolean
  /** Missing policy means enabled, preserving old configurations. */
  allowCellOverrides?: (field: string) => boolean
}

export interface CellFormulaRuntimeOptions<Row extends object> {
  sheets: readonly CellFormulaRuntimeSheet<Row>[]
}

export type FormulaCandidateResult =
  | { ok: true; value: FormulaValue }
  | { ok: false; kind: 'syntax'; error: FormulaSyntaxError }
  | { ok: false; kind: 'cycle'; path: readonly FormulaCellCoordinate[] }

export interface CellFormulaRuntime {
  valueOf: (coordinate: FormulaCellCoordinate) => FormulaValue
  validateCandidate: (coordinate: FormulaCellCoordinate, formula: StoredFormula) => FormulaCandidateResult
  hasStableRowIds: (sheetId: string) => boolean
  invalidate: () => void
}

interface IndexedSheet<Row extends object> {
  source: CellFormulaRuntimeSheet<Row>
  rowById: ReadonlyMap<string, Row>
  rowIndexById: ReadonlyMap<string, number>
  fieldIndex: ReadonlyMap<string, number>
  stable: boolean
}

const coordinateKey = ({ sheetId, rowId, field }: FormulaCellCoordinate): string =>
  `${sheetId.length}:${sheetId}${rowId.length}:${rowId}${field.length}:${field}`

const sameCoordinate = (a: FormulaCellCoordinate, b: FormulaCellCoordinate): boolean =>
  a.sheetId === b.sheetId && a.rowId === b.rowId && a.field === b.field

/**
 * Builds a pure recursive evaluator over a workbook snapshot. It unifies calculated-column
 * formulas and per-cell formulas in one memo/recursion guard, so a dependency always observes
 * the effective value (formula override → manual/blank override → column formula → raw value).
 */
export function createCellFormulaRuntime<Row extends object>(
  options: CellFormulaRuntimeOptions<Row>,
): CellFormulaRuntime {
  const sheets = new Map<string, IndexedSheet<Row>>()

  for (const source of options.sheets) {
    const rowById = new Map<string, Row>()
    const rowIndexById = new Map<string, number>()
    let stable = true
    source.rows.forEach((row, index) => {
      const id = source.rowId(row)
      if (id === undefined || id.length === 0 || rowById.has(id)) {
        stable = false
        return
      }
      rowById.set(id, row)
      rowIndexById.set(id, index)
    })
    if (rowById.size !== source.rows.length) stable = false

    const fieldIndex = new Map<string, number>()
    source.fields.forEach((field, index) => {
      if (!fieldIndex.has(field)) fieldIndex.set(field, index)
    })
    sheets.set(source.id, { source, rowById, rowIndexById, fieldIndex, stable })
  }

  let memo = new Map<string, FormulaValue>()

  const evaluateRoot = (
    root: FormulaCellCoordinate,
    candidate?: { coordinate: FormulaCellCoordinate; formula: StoredFormula },
  ): { value: FormulaValue; cycle: readonly FormulaCellCoordinate[] | null } => {
    const stack: FormulaCellCoordinate[] = []
    const active = new Map<string, number>()
    let cycle: readonly FormulaCellCoordinate[] | null = null

    const evaluateCoordinate = (coordinate: FormulaCellCoordinate): FormulaValue => {
      const key = coordinateKey(coordinate)
      const cycleStart = active.get(key)
      if (cycleStart !== undefined) {
        cycle ??= [...stack.slice(cycleStart), coordinate]
        return error('#¡REF!')
      }

      const useCandidate = candidate !== undefined && sameCoordinate(candidate.coordinate, coordinate)
      if (candidate === undefined) {
        const cached = memo.get(key)
        if (cached !== undefined) return cached
      }

      const sheet = sheets.get(coordinate.sheetId)
      if (sheet === undefined || !sheet.stable || !sheet.fieldIndex.has(coordinate.field)) {
        return error('#¡REF!')
      }
      const row = sheet.rowById.get(coordinate.rowId)
      if (row === undefined) return error('#¡REF!')

      active.set(key, stack.length)
      stack.push(coordinate)

      const allowed = sheet.source.allowCellOverrides?.(coordinate.field) === false
        ? undefined
        : sheet.source.overrideFor?.(coordinate.rowId, coordinate.field)
      // A manual value sits over a column formula, never over a typed cell: the grid shows what
      // is stored there, so reading a leftover from when the column was calculated would make
      // formulas disagree with the cell on screen.
      const override = allowed?.kind === 'formula' || sheet.source.formulaForField?.(coordinate.field) !== undefined
        ? allowed
        : undefined
      const formula = useCandidate
        ? candidate.formula
        : override?.kind === 'formula'
          ? override.formula
          : undefined

      let result: FormulaValue
      if (formula !== undefined) {
        result = evaluateStored(formula, coordinate, evaluateCoordinate)
      } else if (override?.kind === 'value') {
        result = fromCell(override.value)
      } else if (override?.kind === 'blank') {
        result = fromCell(null)
      } else {
        const columnFormula = sheet.source.cycleInput?.(row, coordinate.field)
          ? undefined
          : sheet.source.formulaForField?.(coordinate.field)
        result = columnFormula === undefined
          ? fromCell(sheet.source.rawValue(row, coordinate.field))
          : evaluateStored(columnFormula, coordinate, evaluateCoordinate)
      }

      stack.pop()
      active.delete(key)
      if (candidate === undefined) memo.set(key, result)
      return result
    }

    const evaluateStored = (
      formula: StoredFormula,
      owner: FormulaCellCoordinate,
      resolveCell: (coordinate: FormulaCellCoordinate) => FormulaValue,
    ): FormulaValue => {
      // `fromStored` never throws: an unreadable formula decodes to a `raw` node, which the
      // evaluator turns into `#¡NOMBRE?` on its own.
      return evaluateCellFormula(fromStored(formula), {
        resolveReference: (reference, intent) => resolveReference(owner, reference, intent, resolveCell),
        resolveRange: (from, to) => resolveRange(owner, from, to, resolveCell),
        rowNumberOf: (reference) => {
          const sheet = targetSheet(owner, reference)
          const rowId = rowIdForReference(owner, reference)
          const index = sheet === undefined || rowId === undefined ? undefined : sheet.rowIndexById.get(rowId)
          return index === undefined ? undefined : index + 1
        },
      })
    }

    const targetSheet = (
      owner: FormulaCellCoordinate,
      reference: FormulaReference,
    ): IndexedSheet<Row> | undefined =>
      // Nothing outside the current document is loaded, so a reference into one resolves to no
      // sheet at all and the caller reports `#¡REF!`. The grammar, the serializer and the editor
      // all handle these; only evaluation is waiting on the cross-document loading pipeline.
      reference.documentId !== undefined ? undefined : sheets.get(reference.sheetId ?? owner.sheetId)

    const rowIdForReference = (
      owner: FormulaCellCoordinate,
      reference: FormulaReference,
    ): string | undefined => {
      if (reference.row === 'current' || reference.row === 'column') return owner.rowId
      return reference.row.id
    }

    const resolveReference = (
      owner: FormulaCellCoordinate,
      reference: FormulaReference,
      intent: FormulaResolveIntent,
      resolveCell: (coordinate: FormulaCellCoordinate) => FormulaValue,
    ): FormulaValue => {
      const sheet = targetSheet(owner, reference)
      if (sheet === undefined || !sheet.stable || !sheet.fieldIndex.has(reference.field)) return error('#¡REF!')

      if (intent === 'collection') {
        // Explicit/current-row references remain scalar arguments to SUM. A whole-column
        // reference expands across the persisted row order.
        if (reference.row !== 'column') {
          const rowId = rowIdForReference(owner, reference)
          return rowId === undefined
            ? error('#¡REF!')
            : resolveCell({ sheetId: sheet.source.id, rowId, field: reference.field })
        }
        return collection(sheet.source.rows.map((row) => {
          const rowId = sheet.source.rowId(row)
          return rowId === undefined
            ? error('#¡REF!')
            : resolveCell({ sheetId: sheet.source.id, rowId, field: reference.field })
        }))
      }

      // Direct cross-sheet scalar references are deliberately outside the v2 contract.
      if (reference.sheetId !== undefined && reference.sheetId !== owner.sheetId) return error('#¡REF!')
      const rowId = rowIdForReference(owner, reference)
      return rowId === undefined
        ? error('#¡REF!')
        : resolveCell({ sheetId: sheet.source.id, rowId, field: reference.field })
    }

    const resolveRange = (
      owner: FormulaCellCoordinate,
      from: FormulaReference,
      to: FormulaReference,
      resolveCell: (coordinate: FormulaCellCoordinate) => FormulaValue,
    ): FormulaValue => {
      const fromSheetId = from.sheetId ?? owner.sheetId
      const toSheetId = to.sheetId ?? fromSheetId
      if (fromSheetId !== toSheetId) return error('#¡REF!')
      const sheet = sheets.get(fromSheetId)
      if (sheet === undefined || !sheet.stable) return error('#¡REF!')
      const firstField = sheet.fieldIndex.get(from.field)
      const lastField = sheet.fieldIndex.get(to.field)
      if (firstField === undefined || lastField === undefined) return error('#¡REF!')

      const fieldStart = Math.min(firstField, lastField)
      const fieldEnd = Math.max(firstField, lastField)
      const allRows = from.row === 'column' && to.row === 'column'
      let rowStart = 0
      let rowEnd = sheet.source.rows.length - 1
      if (!allRows) {
        const fromRowId = rowIdForReference(owner, from)
        const toRowId = rowIdForReference(owner, to)
        const firstRow = fromRowId === undefined ? undefined : sheet.rowIndexById.get(fromRowId)
        const lastRow = toRowId === undefined ? undefined : sheet.rowIndexById.get(toRowId)
        if (firstRow === undefined || lastRow === undefined) return error('#¡REF!')
        rowStart = Math.min(firstRow, lastRow)
        rowEnd = Math.max(firstRow, lastRow)
      }

      const values: FormulaValue[] = []
      for (let rowIndex = rowStart; rowIndex <= rowEnd; rowIndex++) {
        const row = sheet.source.rows[rowIndex]
        if (row === undefined) return error('#¡REF!')
        const rowId = sheet.source.rowId(row)
        if (rowId === undefined) return error('#¡REF!')
        for (let fieldIndex = fieldStart; fieldIndex <= fieldEnd; fieldIndex++) {
          const field = sheet.source.fields[fieldIndex]
          if (field === undefined) return error('#¡REF!')
          values.push(resolveCell({ sheetId: sheet.source.id, rowId, field }))
        }
      }
      return collection(values, fieldEnd - fieldStart + 1)
    }

    return { value: evaluateCoordinate(root), cycle }
  }

  return {
    valueOf: (coordinate) => evaluateRoot(coordinate).value,
    validateCandidate: (coordinate, formula) => {
      // A candidate normally arrives already parsed — it is built from an AST the editor just
      // accepted — so the usual job here is only to see whether evaluating it closes a cycle.
      // A `raw` one is the exception: it is text that never parsed, kept so the user can repair
      // it, and it is reported as the syntax problem it is.
      const ast = fromStored(formula)
      if (ast.kind === 'raw') {
        return {
          ok: false,
          kind: 'syntax',
          error: { message: 'La fórmula tiene un error de sintaxis.', start: 0, end: ast.text.length },
        }
      }
      const evaluated = evaluateRoot(coordinate, { coordinate, formula })
      return evaluated.cycle === null
        ? { ok: true, value: evaluated.value }
        : { ok: false, kind: 'cycle', path: evaluated.cycle }
    },
    hasStableRowIds: (sheetId) => sheets.get(sheetId)?.stable ?? false,
    invalidate: () => {
      memo = new Map()
    },
  }
}
